import React, { useState, useEffect } from "react";
import { X, Activity, Calendar, User, MapPin, Smartphone } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Vital, getVitalKey } from "@/lib/dashboard/vitalsData";
import { Skeleton } from "@/components/controls/customControls/skeleton";
import { format } from "date-fns";
import { getVitalColor } from "@/lib/dashboard/vitalsDetailData";

interface VitalsDetailModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  vital: Vital | null;
  isDarkMode?: boolean;
  loading?: boolean;
  chartData?: any | null;
}

interface TooltipData {
  x: number;
  y: number;
  displayValue: string;
  displayUnit?: string;
  date: string;
  visible: boolean;
  createdBy?: string;
}

// Helper function to extract numeric value from mixed units for chart plotting
const extractChartValue = (
  value: string | number,
  vitalType: string,
  unit?: string,
): number | null => {
  if (typeof value === "number") {
    if (vitalType === "weight" && unit?.toLowerCase() === "lbs") {
      return Math.round(value * 0.453592 * 100) / 100;
    }
    return value;
  }

  const strValue = String(value).trim();

  // Handle height conversions
  if (vitalType === "height") {
    // Feet/inches format like "6 ft 6 in" or "5 ft 8 in"
    if (strValue.includes("ft") || strValue.includes("in")) {
      const feetMatch = strValue.match(/(\d+)\s*ft/);
      const inchesMatch = strValue.match(/(\d+)\s*in/);

      const feet = feetMatch ? parseInt(feetMatch[1]) : 0;
      const inches = inchesMatch ? parseInt(inchesMatch[1]) : 0;
      const totalInches = feet * 12 + inches;

      // Convert to cm for consistent chart scaling
      return Math.round(totalInches * 2.54);
    }

    // Assume cm for numeric values
    const num = Number(strValue);
    return !isNaN(num) && isFinite(num) ? num : null;
  }

  // Handle blood pressure
  if (vitalType === "blood-pressure" && strValue.includes("/")) {
    const [systolicStr] = strValue.split("/");
    const systolic = Number(systolicStr);
    return !isNaN(systolic) && systolic >= 70 && systolic <= 250
      ? systolic
      : null;
  }

  // Handle weight unit conversion (lbs → kg for consistent chart)
  if (vitalType === "weight" && unit?.toLowerCase() === "lbs") {
    const num = Number(strValue);
    if (!isNaN(num) && isFinite(num)) {
      return Math.round(num * 0.453592 * 100) / 100;
    }
    return null;
  }

  // Handle other numeric values
  const num = Number(strValue);
  return !isNaN(num) && isFinite(num) ? num : null;
};

// Helper function to determine display unit - returns empty string if unit is already in the value
const getDisplayUnit = (
  value: string | number,
  vitalType: string,
  defaultUnit: string,
): string => {
  if (typeof value === "number") return defaultUnit;

  const strValue = String(value).trim();

  if (vitalType === "height") {
    // If value already contains units (ft or in), return empty string
    if (strValue.includes("ft") || strValue.includes("in")) {
      return ""; // Empty string since unit is already in the value
    }
    return "cm";
  }

  if (vitalType === "blood-pressure" && strValue.includes("/")) {
    return "mmHg";
  }

  return defaultUnit;
};

// Helper function to fix average value and unit
const fixAverageUnit = (
  average: string | number,
  vitalType: string,
  defaultUnit: string,
): { value: string; unit: string } => {
  if (!average && average !== 0) return { value: "-", unit: "" };

  const avgStr = String(average);
  const avgNum = parseFloat(avgStr);

  if (isNaN(avgNum)) return { value: avgStr, unit: "" };

  // Handle different vital types
  switch (vitalType) {
    case "heart-rate":
      // Heart rate should be 40-200 BPM
      if (avgNum > 200) {
        // Might be in wrong unit - divide by 100
        const corrected = avgNum / 100;
        if (corrected >= 40 && corrected <= 200) {
          return { value: Math.round(corrected).toString(), unit: "BPM" };
        }
      }
      return { value: Math.round(avgNum).toString(), unit: "BPM" };

    case "height":
      // Height average should be in cm
      return { value: Math.round(avgNum).toString(), unit: "cm" };

    case "temperature":
      // Temperature depends on unit
      // Check if it's likely Celsius or Fahrenheit
      if (avgNum > 45) {
        // Too high for Celsius, might be Fahrenheit
        const celsius = Math.round(((avgNum - 32) * 5) / 9);
        return { value: celsius.toString(), unit: "°C" };
      }
      return { value: Math.round(avgNum).toString(), unit: "°C" };

    case "blood-pressure":
      return { value: Math.round(avgNum).toString(), unit: "mmHg" };

    case "oxygen-saturation":
      return { value: Math.round(avgNum).toString(), unit: "%" };

    default:
      return { value: Math.round(avgNum).toString(), unit: defaultUnit };
  }
};

// Helper function to get chart unit for Y-axis labels
const getChartUnit = (vitalType: string): string => {
  switch (vitalType) {
    case "height":
      return "cm";
    case "weight":
      return "kg";
    case "heart-rate":
      return "BPM";
    case "temperature":
      return "°C";
    case "blood-pressure":
      return "mmHg";
    case "oxygen-saturation":
      return "%";
    case "news-pain-score":
      return "/10";
    case "blood-sugar":
      return "mmol/L";
    case "respiratory-rate":
      return "br/min";
    default:
      return "";
  }
};

// Helper function to deduplicate readings.
//
// Weight & height are duplicated by BMI auto-creation: when a standalone
// weight (or height) is added, the system also creates a BMI record that
// embeds the same value.  The API returns readings from both sources, so we
// collapse entries that share the same value on the same calendar day.
//
// Other vitals (HR, BP, temp, etc.) are never auto-duplicated, so we only
// remove true consecutive duplicates (same value within 30 s) — this
// preserves legitimate repeat readings taken minutes or hours apart.
//
// Pass vitalType so weight/height get the broader dedup.  BP and
// circumference charts call without vitalType and get the 30 s dedup.
const deduplicateReadings = (
  readings: any[],
  vitalType?: string,
): any[] => {
  if (!Array.isArray(readings) || readings.length <= 1) return readings;

  // --- weight / height: same value + same calendar day = duplicate ----------
  if (vitalType === "weight" || vitalType === "height") {
    const seen = new Set<string>();
    return readings.filter((r) => {
      const val = String(r.displayValue ?? r.values ?? r.value ?? "");
      const dateField = r.date || r.createdOn;
      const dateStr = new Date(dateField).toLocaleDateString();
      const key = `${val}::${dateStr}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  // --- bmi or unspecified: 30 s consecutive dedup ---------------------------
  if (vitalType === "bmi" || !vitalType) {
    const deduplicated: any[] = [];
    for (let i = 0; i < readings.length; i++) {
      const current = readings[i];
      const currentValue = String(
        current.displayValue ?? current.values ?? current.value ?? "",
      );
      const currentDateField = current.date || current.createdOn;
      const currentDate = new Date(currentDateField);
      const currentDateString = currentDate.toLocaleDateString();

      if (i + 1 < readings.length) {
        const next = readings[i + 1];
        const nextValue = String(
          next.displayValue ?? next.values ?? next.value ?? "",
        );
        const nextDateField = next.date || next.createdOn;
        const nextDate = new Date(nextDateField);
        const nextDateString = nextDate.toLocaleDateString();

        if (
          currentValue === nextValue &&
          currentDateString === nextDateString
        ) {
          const timeDiffSeconds =
            Math.abs(nextDate.getTime() - currentDate.getTime()) / 1000;
          if (timeDiffSeconds < 30) {
            continue;
          }
        }
      }
      deduplicated.push(current);
    }
    return deduplicated;
  }

  // --- all other vitals: no dedup (preserves legitimate repeat readings) ----
  return readings;
};

// Helper to normalise supplemental oxygen raw values to "Yes" / "No"
const normaliseYesNo = (raw: unknown): "Yes" | "No" => {
  const str = String(raw ?? "")
    .toLowerCase()
    .trim();
  if (str === "true" || str === "yes" || str === "1" || str === "on")
    return "Yes";
  return "No";
};

export default function InteractiveChart({
  data,
  vital,
  isDarkMode = false,
  chartUnit = "",
}: {
  data: any[];
  vital: Vital;
  isDarkMode?: boolean;
  chartUnit?: string;
}) {
  const [hoveredPoint, setHoveredPoint] = useState<TooltipData>({
    x: 0,
    y: 0,
    displayValue: "",
    displayUnit: "",
    date: "",
    visible: false,
    createdBy: undefined,
  });
  const [animationProgress, setAnimationProgress] = useState(0);
  const [pathLength, setPathLength] = useState(0);

  // normalize and sort by ISO timestamp
  const normalized = deduplicateReadings(
    data
      .map((d) => ({
        ...d,
        _ts: d.date
          ? typeof d.date === "number"
            ? d.date
            : Date.parse(d.date)
          : NaN,
        _label: d.labelDate || d.date || String(d.raw?.createdOn || ""),
        _createdBy: d.createdBy || d.raw?.createdBy || "",
        _displayValue: d.displayValue || String(d.value),
        _displayUnit: d.displayUnit || vital.unit,
      }))
      .filter(
        (d) =>
          d.value !== null &&
          typeof d.value === "number" &&
          !isNaN(d.value) &&
          isFinite(d.value),
      )
      .sort((a, b) => (a._ts || 0) - (b._ts || 0)),
    vital.type,
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationProgress(1);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  if (normalized.length === 0) {
    return (
      <div className="flex items-center justify-center w-full h-64 border-2 border-dashed rounded-lg">
        <span className="text-gray-500">No trend data available</span>
      </div>
    );
  }

  const rawMax = Math.max(...normalized.map((d) => d.value));
  const rawMin = Math.min(...normalized.map((d) => d.value));
  const singleBuffer = normalized.length === 1 ? Math.max(rawMax * 0.1, 1) : 0;
  const maxValue = rawMax + singleBuffer;
  const minValue = rawMin - singleBuffer;
  const range = maxValue - minValue || 1;

  // Compute nice Y-axis ticks so narrow ranges never produce duplicate labels.
  // e.g. 75–77 kg → step 0.5 → ticks [75.0, 75.5, 76.0, 76.5, 77.0]
  const yAxisStep = (() => {
    if (range <= 0) return 1;
    const rough = range / 4;
    const mag = Math.pow(10, Math.floor(Math.log10(rough)));
    const norm = rough / mag;
    const nice = norm < 1.5 ? 1 : norm < 3.5 ? 2 : norm < 7.5 ? 5 : 10;
    return nice * mag;
  })();
  const yAxisTickStart = Math.floor(minValue / yAxisStep) * yAxisStep;
  const yAxisTickCount =
    Math.round(
      (Math.ceil(maxValue / yAxisStep) * yAxisStep - yAxisTickStart) /
        yAxisStep,
    ) + 1;
  const yAxisTicks = Array.from({ length: yAxisTickCount }, (_, i) =>
    parseFloat((yAxisTickStart + i * yAxisStep).toPrecision(10)),
  ).filter(
    (t) => t >= minValue - yAxisStep * 0.01 && t <= maxValue + yAxisStep * 0.01,
  );
  const yAxisDecimals =
    yAxisStep < 1 ? Math.max(0, Math.ceil(-Math.log10(yAxisStep))) : 0;

  const viewBoxWidth = 800;
  const viewBoxHeight = 300;
  const padding = 65;

  const vitalColor = getVitalColor(vital.type);

  const getColorForValue = (value: number) => {
    if (vital.type === "heart-rate") {
      if (value > 100) return "#dc2626"; // red for high
      if (value < 60) return "#eab308"; // yellow for low
      return "#16a34a"; // green for normal
    }
    if (vital.type === "temperature") {
      if (value > 37.5) return "#dc2626"; // red for fever (> 37.5°C)
      if (value < 36) return "#eab308"; // yellow for low temp
      return "#16a34a"; // green for normal
    }
    return "#16a34a"; // default green
  };

  const isSinglePoint = normalized.length === 1;
  const points = normalized.map((reading, index) => {
    const xRatio = isSinglePoint ? 0.5 : index / (normalized.length - 1);
    const x = padding + xRatio * (viewBoxWidth - 2 * padding);
    const y =
      viewBoxHeight -
      padding -
      ((reading.value - minValue) / range) * (viewBoxHeight - 2 * padding);
    return {
      x,
      y,
      value: reading.value,
      displayValue: reading._displayValue,
      displayUnit: reading._displayUnit,
      date: reading._label,
      createdBy: reading._createdBy,
      raw: reading.raw,
      index,
    };
  });

  const createSmoothPath = (points: any[]) => {
    if (points.length < 2) return "";
    let path = `M ${points[0].x},${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
      const current = points[i];
      const prev = points[i - 1];
      const next = points[i + 1] || current;
      const cp1x = prev.x + (current.x - prev.x) * 0.3;
      const cp1y = prev.y;
      const cp2x = current.x - (next.x - current.x) * 0.3;
      const cp2y = current.y;
      path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${current.x},${current.y}`;
    }
    return path;
  };

  const pathData = createSmoothPath(points);

  const handleMouseMove = (event: React.MouseEvent<SVGElement>) => {
    const svg = event.currentTarget;
    const rect = svg.getBoundingClientRect();
    const mouseX = ((event.clientX - rect.left) / rect.width) * viewBoxWidth;

    const closest = points.reduce((prev, curr) =>
      Math.abs(curr.x - mouseX) < Math.abs(prev.x - mouseX) ? curr : prev,
    );

    setHoveredPoint({
      x: Math.min(rect.width - 12, Math.max(6, event.clientX - rect.left)), // clamp to viewport
      y: Math.max(12, event.clientY - rect.top),
      displayValue: closest.displayValue,
      displayUnit: closest.displayUnit,
      date: format(
        new Date(closest.raw?.createdOn || closest.date),
        "MMM d, yyyy 'at' HH:mm",
      ),
      createdBy: closest.createdBy || undefined,
      visible: true,
    });
  };

  const handleMouseLeave = () =>
    setHoveredPoint((prev) => ({ ...prev, visible: false }));

  const legendConfig: Record<
    string,
    { label: string; color: string; range?: string }[]
  > = {
    "heart-rate": [
      { label: "High", color: "bg-red-500", range: "(100+ BPM)" },
      { label: "Normal", color: "bg-green-500" },
      { label: "Low", color: "bg-yellow-500", range: "(60- BPM)" },
    ],
    "blood-pressure": [
      { label: "High", color: "bg-red-500", range: "(140/90+ mmHg)" },
      { label: "Normal", color: "bg-green-500", range: "(120/80 mmHg)" },
      { label: "Low", color: "bg-yellow-500", range: "(90/60- mmHg)" },
    ],
    temperature: [
      { label: "High", color: "bg-red-500", range: "(> 100°F)" },
      { label: "Normal", color: "bg-green-500", range: "(97–99°F)" },
      { label: "Low", color: "bg-yellow-500", range: "(< 97°F)" },
    ],
  };

  return (
    <div className="relative w-full">
      {/* Legend */}
      {/* <div className="flex gap-4 mb-4 text-sm">
  {(legendConfig[vital.type] || []).map((item, index) => (
    <div key={index} className="flex items-center gap-2">
      <div className={`w-3 h-0.5 ${item.color}`}></div>
      <span className="text-gray-600">
        {item.label} {item.range ? item.range : ""}
      </span>
    </div>
  ))}
</div> */}

      <div className="relative w-full h-80">
        <svg
          viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
          className={`w-full h-full border rounded-lg cursor-crosshair overflow-visible ${
            isDarkMode
              ? "border-gray-700 bg-gray-900"
              : "border-gray-200 bg-white"
          }`}
          preserveAspectRatio="none"
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          {/* Grid */}
          <defs>
            <pattern
              id="grid"
              width="40"
              height="30"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 40 0 L 0 0 0 30"
                fill="none"
                stroke={isDarkMode ? "#374151" : "#f0f0f0"}
                strokeWidth="0.5"
              />
            </pattern>
            <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={vitalColor} stopOpacity="0.12" />
              <stop offset="100%" stopColor={vitalColor} stopOpacity="0.02" />
            </linearGradient>
          </defs>

          <rect width="100%" height="100%" fill="url(#grid)" />

          {/* Axes */}
          <line
            x1={padding}
            y1={viewBoxHeight - padding}
            x2={viewBoxWidth - padding}
            y2={viewBoxHeight - padding}
            stroke={isDarkMode ? "#6b7280" : "#d1d5db"}
            strokeWidth="1.5"
          />
          <line
            x1={padding}
            y1={padding}
            x2={padding}
            y2={viewBoxHeight - padding}
            stroke={isDarkMode ? "#6b7280" : "#d1d5db"}
            strokeWidth="1.5"
          />

          {/* Area under curve */}
          <path
            d={`${pathData} L ${viewBoxWidth - padding},${
              viewBoxHeight - padding
            } L ${padding},${viewBoxHeight - padding} Z`}
            fill="url(#areaGradient)"
          />

          {/* Main line */}
          <path
            d={pathData}
            fill="none"
            stroke={vitalColor}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={pathLength}
            strokeDashoffset={pathLength * (1 - animationProgress)}
            ref={(ref) => {
              if (ref && pathLength === 0) {
                const length = ref.getTotalLength();
                setPathLength(length);
              }
            }}
            style={{
              filter: `drop-shadow(0 2px 8px ${vitalColor})`,
              transition: "stroke-dashoffset 1.5s ease-in-out",
            }}
          />

          {/* Data points */}
          {points.map((p, i) => {
            const color = getColorForValue(p.value);
            const delay = 1500 + i * 100;
            const show = animationProgress > i / points.length;
            return (
              <circle
                key={i}
                cx={p.x}
                cy={p.y}
                r="4"
                fill={color}
                stroke="white"
                strokeWidth="2"
                opacity={show ? 1 : 0}
                style={{
                  filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.1))",
                  transition: `opacity 0.3s ease ${delay}ms`,
                  transform: show ? "scale(1)" : "scale(0)",
                }}
              />
            );
          })}

          {/* Y-axis labels */}
          {yAxisTicks.map((tickVal) => {
            const pct = (tickVal - minValue) / range;
            const y =
              viewBoxHeight - padding - pct * (viewBoxHeight - 2 * padding);
            return (
              <g key={tickVal}>
                <line
                  x1={padding - 5}
                  y1={y}
                  x2={padding}
                  y2={y}
                  stroke={isDarkMode ? "#6b7280" : "#d1d5db"}
                />
                <text
                  x={padding - 10}
                  y={y + 3}
                  textAnchor="end"
                  className={`text-xs ${
                    isDarkMode ? "fill-gray-400" : "fill-gray-600"
                  }`}
                  fontSize="10"
                >
                  {tickVal.toFixed(yAxisDecimals)}
                  {chartUnit ? ` ${chartUnit}` : ""}
                </text>
              </g>
            );
          })}

          {/* X-axis labels (compact & spaced) */}
          {(() => {
            const maxLabels = Math.min(8, normalized.length);
            const step = Math.max(1, Math.ceil(normalized.length / maxLabels));
            return normalized.map((row, i) => {
              // ensure last label always shows
              if (i % step !== 0 && i !== normalized.length - 1) return null;
              const x =
                padding +
                (normalized.length > 1 ? i / (normalized.length - 1) : 0.5) *
                  (viewBoxWidth - 2 * padding);
              const dateObj = new Date(row._ts || row._label || row.date);
              const shortLabel = format(dateObj, "MMM d");
              const yearLabel = format(dateObj, "yyyy");
              const yPos = viewBoxHeight - padding + 30;

              return (
                <g key={i}>
                  <line
                    x1={x}
                    y1={viewBoxHeight - padding}
                    x2={x}
                    y2={viewBoxHeight - padding + 5}
                    stroke={isDarkMode ? "#6b7280" : "#d1d5db"}
                  />
                  <text
                    x={x}
                    y={yPos}
                    transform={`rotate(-40 ${x} ${yPos})`}
                    textAnchor="end"
                    className={`text-xs ${
                      isDarkMode ? "fill-gray-400" : "fill-gray-600"
                    }`}
                    fontSize="10"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    <tspan x={x} dy="0">
                      {shortLabel}
                    </tspan>
                    <tspan x={x} dy="12" className="opacity-70" fontSize="9">
                      {yearLabel}
                    </tspan>
                  </text>
                </g>
              );
            });
          })()}

          {/* Hover line */}
          {hoveredPoint.visible && (
            <line
              x1={hoveredPoint.x / (viewBoxWidth / 500)}
              y1={padding}
              x2={hoveredPoint.x / (viewBoxWidth / 500)}
              y2={viewBoxHeight - padding}
              stroke={isDarkMode ? "#6b7280" : "#9ca3af"}
              strokeWidth="1"
              strokeDasharray="4,4"
              opacity="0.7"
            />
          )}
        </svg>

        {/* Tooltip */}
        {hoveredPoint.visible && (
          <div
            className="absolute z-10 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg shadow-lg pointer-events-none"
            style={{
              left: hoveredPoint.x + 10,
              top: hoveredPoint.y - 10,
              transform: "translateY(-100%)",
            }}
          >
            <div className="font-semibold">{hoveredPoint.date}</div>
            {hoveredPoint.createdBy ? (
              <div className="text-xs opacity-90">
                By: {hoveredPoint.createdBy}
              </div>
            ) : null}
            <div className="mt-1">
              <strong>{vital.label}:</strong> {hoveredPoint.displayValue}{" "}
              {hoveredPoint.displayUnit}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * VitalsDetailModal - A standalone, reusable component for displaying detailed vital sign information
 *
 * Features:
 * - Interactive animated chart with smooth line drawing
 * - Hover tooltips and crosshair indicators
 * - Recent readings with detailed provider and device information
 * - Statistics summary (Latest, Average, Total Readings)
 * - Dark mode support
 * - Responsive layout with chart on left, readings on right
 *
 * @param isOpen - Controls modal visibility
 * @param onOpenChange - Callback when modal open state changes
 * @param vital - Vital sign data object with historical data
 * @param isDarkMode - Optional dark mode styling
 */

function BloodPressureChart({
  data,
  isDarkMode = false,
}: {
  data: any[];
  isDarkMode?: boolean;
}) {
  const [hoveredPoint, setHoveredPoint] = useState<{
    x: number;
    y: number;
    visible: boolean;
    systolic: string;
    diastolic: string;
    date: string;
    createdBy?: string;
  }>({
    x: 0,
    y: 0,
    visible: false,
    systolic: "",
    diastolic: "",
    date: "",
    createdBy: undefined,
  });

  const parsed = deduplicateReadings(
    data
      .map((d) => {
        const str = String(d.displayValue ?? d.value ?? "");
        const parts = str.split("/");
        const sys = Number(parts[0]);
        const dia = Number(parts[1]);
        return {
          ...d,
          sys: !isNaN(sys) ? sys : null,
          dia: !isNaN(dia) ? dia : null,
          _ts: d.date
            ? typeof d.date === "number"
              ? d.date
              : Date.parse(d.date)
            : NaN,
        };
      })
      .filter((d) => d.sys !== null && d.dia !== null)
      .sort((a, b) => (a._ts || 0) - (b._ts || 0)),
  );

  if (parsed.length === 0) {
    return (
      <div className="flex items-center justify-center w-full h-64 border-2 border-dashed rounded-lg">
        <span className="text-gray-500">No blood pressure data available</span>
      </div>
    );
  }

  const allValues = [
    ...parsed.map((d) => d.sys!),
    ...parsed.map((d) => d.dia!),
  ];
  const rawMaxVal = Math.max(...allValues);
  const rawMinVal = Math.min(...allValues);
  const bpSingleBuffer = parsed.length === 1 ? Math.max(rawMaxVal * 0.1, 1) : 0;
  const maxVal = rawMaxVal + bpSingleBuffer;
  const minVal = rawMinVal - bpSingleBuffer;
  const range = maxVal - minVal || 1;

  const W = 800,
    H = 300,
    PAD = 50;

  const toPoint = (index: number, value: number) => ({
    x:
      PAD +
      (parsed.length > 1 ? index / (parsed.length - 1) : 0.5) * (W - 2 * PAD),
    y: H - PAD - ((value - minVal) / range) * (H - 2 * PAD),
  });

  const buildPath = (values: number[]) => {
    const pts = values.map((v, i) => toPoint(i, v));
    if (pts.length < 2) return "";
    let path = `M ${pts[0].x},${pts[0].y}`;
    for (let i = 1; i < pts.length; i++) {
      const p = pts[i],
        prev = pts[i - 1];
      const cp1x = prev.x + (p.x - prev.x) * 0.4;
      path += ` C ${cp1x},${prev.y} ${p.x - (p.x - prev.x) * 0.4},${p.y} ${p.x},${p.y}`;
    }
    return path;
  };

  const sysPath = buildPath(parsed.map((d) => d.sys!));
  const diaPath = buildPath(parsed.map((d) => d.dia!));

  const handleMouseMove = (e: React.MouseEvent<SVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = ((e.clientX - rect.left) / rect.width) * W;
    const closest = parsed.reduce((prev, curr, i) => {
      const px =
        PAD +
        (parsed.length > 1 ? i / (parsed.length - 1) : 0.5) * (W - 2 * PAD);
      const prevIdx = parsed.indexOf(prev);
      const prevX =
        PAD +
        (parsed.length > 1 ? prevIdx / (parsed.length - 1) : 0.5) *
          (W - 2 * PAD);
      return Math.abs(px - mouseX) < Math.abs(prevX - mouseX) ? curr : prev;
    });
    setHoveredPoint({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      visible: true,
      systolic: String(closest.sys),
      diastolic: String(closest.dia),
      date: format(
        new Date(closest.raw?.createdOn || closest.date),
        "MMM d, yyyy 'at' HH:mm",
      ),
      createdBy: closest.raw?.createdBy,
    });
  };

  return (
    <div className="relative w-full">
      {/* Legend */}
      <div className="flex gap-6 mb-3 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-6 h-0.5 bg-red-500 rounded" />
          <span className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
            Systolic
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-0.5 bg-blue-500 rounded" />
          <span className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
            Diastolic
          </span>
        </div>
      </div>

      <div className="relative w-full h-80">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className={`w-full h-full border rounded-lg cursor-crosshair overflow-visible ${
            isDarkMode
              ? "border-gray-700 bg-gray-900"
              : "border-gray-200 bg-white"
          }`}
          preserveAspectRatio="none"
          onMouseMove={handleMouseMove}
          onMouseLeave={() =>
            setHoveredPoint((p) => ({ ...p, visible: false }))
          }
        >
          <defs>
            <pattern
              id="bp-grid"
              width="40"
              height="30"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 40 0 L 0 0 0 30"
                fill="none"
                stroke={isDarkMode ? "#374151" : "#f0f0f0"}
                strokeWidth="0.5"
              />
            </pattern>
            <linearGradient id="sysGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#ef4444" stopOpacity="0.10" />
              <stop offset="100%" stopColor="#ef4444" stopOpacity="0.01" />
            </linearGradient>
            <linearGradient id="diaGrad" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.08" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.01" />
            </linearGradient>
          </defs>
          <rect width="100%" height="100%" fill="url(#bp-grid)" />

          {/* Axes */}
          <line
            x1={PAD}
            y1={H - PAD}
            x2={W - PAD}
            y2={H - PAD}
            stroke={isDarkMode ? "#6b7280" : "#d1d5db"}
            strokeWidth="1.5"
          />
          <line
            x1={PAD}
            y1={PAD}
            x2={PAD}
            y2={H - PAD}
            stroke={isDarkMode ? "#6b7280" : "#d1d5db"}
            strokeWidth="1.5"
          />

          {/* Systolic area + line */}
          <path
            d={`${sysPath} L ${PAD + ((parsed.length - 1) / (parsed.length - 1)) * (W - 2 * PAD)},${H - PAD} L ${PAD},${H - PAD} Z`}
            fill="url(#sysGrad)"
          />
          <path
            d={sysPath}
            fill="none"
            stroke="#ef4444"
            strokeWidth="3"
            strokeLinecap="round"
            style={{ filter: "drop-shadow(0 2px 6px #ef4444aa)" }}
          />
          <path
            d={`${diaPath} L ${PAD + ((parsed.length - 1) / (parsed.length - 1)) * (W - 2 * PAD)},${H - PAD} L ${PAD},${H - PAD} Z`}
            fill="url(#diaGrad)"
          />

          {/* Diastolic line */}
          <path
            d={diaPath}
            fill="none"
            stroke="#3b82f6"
            strokeWidth="3"
            strokeLinecap="round"
            style={{ filter: "drop-shadow(0 2px 6px #3b82f6aa)" }}
          />

          {/* Dots - Systolic */}
          {parsed.map((d, i) => {
            const p = toPoint(i, d.sys!);
            return (
              <circle
                key={`s${i}`}
                cx={p.x}
                cy={p.y}
                r="4"
                fill="#ef4444"
                stroke="white"
                strokeWidth="2"
              />
            );
          })}
          {/* Dots - Diastolic */}
          {parsed.map((d, i) => {
            const p = toPoint(i, d.dia!);
            return (
              <circle
                key={`d${i}`}
                cx={p.x}
                cy={p.y}
                r="4"
                fill="#3b82f6"
                stroke="white"
                strokeWidth="2"
              />
            );
          })}

          {/* Y-axis labels */}
          {[0, 25, 50, 75, 100].map((pct) => {
            const val = minVal + (pct / 100) * range;
            const y = H - PAD - (pct / 100) * (H - 2 * PAD);
            return (
              <g key={pct}>
                <line
                  x1={PAD - 5}
                  y1={y}
                  x2={PAD}
                  y2={y}
                  stroke={isDarkMode ? "#6b7280" : "#d1d5db"}
                />
                <text
                  x={PAD - 10}
                  y={y + 3}
                  textAnchor="end"
                  fontSize="10"
                  className={isDarkMode ? "fill-gray-400" : "fill-gray-600"}
                >
                  {/* {Math.round(val)} mmHg */}
                  {Math.round(val)}
                </text>
              </g>
            );
          })}

          {/* X-axis labels */}
          {/* {parsed.map((d, i) => {
            const maxLabels = Math.min(8, parsed.length);
            const step = Math.max(1, Math.ceil(parsed.length / maxLabels));
            if (i % step !== 0 && i !== parsed.length - 1) return null;
            const x = PAD + (i / (parsed.length - 1)) * (W - 2 * PAD);
            const dateObj = new Date(d._ts || d.date);
            const yPos = H - PAD + 30;
            return (
              <g key={i}>
                <line
                  x1={x}
                  y1={H - PAD}
                  x2={x}
                  y2={H - PAD + 5}
                  stroke={isDarkMode ? "#6b7280" : "#d1d5db"}
                />
                <text
                  x={x}
                  y={yPos}
                  transform={`rotate(-40 ${x} ${yPos})`}
                  textAnchor="end"
                  fontSize="10"
                  className={isDarkMode ? "fill-gray-400" : "fill-gray-600"}
                >
                  <tspan x={x} dy="0">
                    {format(dateObj, "MMM d")}
                  </tspan>
                  <tspan x={x} dy="12" fontSize="9">
                    {format(dateObj, "yyyy")}
                  </tspan>
                </text>
              </g>
            );
          })} */}
          {parsed.map((d, i) => {
            const maxLabels = Math.min(8, parsed.length);
            const step = Math.max(1, Math.ceil(parsed.length / maxLabels));
            if (i % step !== 0 && i !== parsed.length - 1) return null;
            const x =
              PAD +
              (parsed.length > 1 ? i / (parsed.length - 1) : 0.5) *
                (W - 2 * PAD);
            const dateObj = new Date(d._ts || d.date);
            return (
              <g key={i}>
                <line
                  x1={x}
                  y1={H - PAD}
                  x2={x}
                  y2={H - PAD + 5}
                  stroke={isDarkMode ? "#6b7280" : "#d1d5db"}
                />
                <text
                  x={x}
                  y={H - PAD + 18}
                  textAnchor="middle"
                  fontSize="10"
                  className={isDarkMode ? "fill-gray-400" : "fill-gray-600"}
                >
                  {format(dateObj, "MMM d")}
                </text>
              </g>
            );
          })}
          {hoveredPoint.visible && (
            <line
              x1={hoveredPoint.x / (W / 500)}
              y1={PAD}
              x2={hoveredPoint.x / (W / 500)}
              y2={H - PAD}
              stroke={isDarkMode ? "#6b7280" : "#9ca3af"}
              strokeWidth="1"
              strokeDasharray="4,4"
              opacity="0.7"
            />
          )}
        </svg>

        {/* Tooltip */}
        {hoveredPoint.visible && (
          <div
            className="absolute z-10 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg shadow-lg pointer-events-none"
            style={{
              left: hoveredPoint.x + 12,
              top: hoveredPoint.y - 10,
              transform: "translateY(-100%)",
            }}
          >
            <div className="mb-1 font-semibold">{hoveredPoint.date}</div>
            {hoveredPoint.createdBy && (
              <div className="mb-1 opacity-80">
                By: {hoveredPoint.createdBy}
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-red-400 rounded-full" />
              <span>
                Systolic: <strong>{hoveredPoint.systolic} mmHg</strong>
              </span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="inline-block w-2 h-2 bg-blue-400 rounded-full" />
              <span>
                Diastolic: <strong>{hoveredPoint.diastolic} mmHg</strong>
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CircumferenceChart({
  data,
  isDarkMode = false,
}: {
  data: any[];
  isDarkMode?: boolean;
}) {
  const [hoveredPoint, setHoveredPoint] = useState<{
    x: number;
    y: number;
    visible: boolean;
    waist: string;
    hip: string;
    neck: string;
    date: string;
    createdBy?: string;
  }>({
    x: 0,
    y: 0,
    visible: false,
    waist: "",
    hip: "",
    neck: "",
    date: "",
    createdBy: undefined,
  });

  const parseCircumference = (str: string) => {
    const waistMatch = str.match(/Waist:\s*(\d+\.?\d*)/i);
    const hipMatch = str.match(/Hip:\s*(\d+\.?\d*)/i);
    const neckMatch = str.match(/Neck:\s*(\d+\.?\d*)/i);
    return {
      waist: waistMatch ? Number(waistMatch[1]) : null,
      hip: hipMatch ? Number(hipMatch[1]) : null,
      neck: neckMatch ? Number(neckMatch[1]) : null,
    };
  };

  const parsed = deduplicateReadings(
    data
      .map((d) => {
        const str = String(d.values ?? d.displayValue ?? d.value ?? "");
        const { waist, hip, neck } = parseCircumference(str);
        return {
          ...d,
          waist,
          hip,
          neck,
          _ts: d.createdOn
            ? Date.parse(d.createdOn)
            : d.date
              ? typeof d.date === "number"
                ? d.date
                : Date.parse(d.date)
              : NaN,
          _label: d.createdOn || d.date || "",
          _createdBy: d.createdBy || "",
          _displayValue: str,
        };
      })
      .filter((d) => d.waist !== null || d.hip !== null || d.neck !== null)
      .sort((a, b) => (a._ts || 0) - (b._ts || 0)),
  );

  if (parsed.length === 0) {
    return (
      <div className="flex items-center justify-center w-full h-64 border-2 border-dashed rounded-lg">
        <span className="text-gray-500">No circumference data available</span>
      </div>
    );
  }

  const allValues = [
    ...parsed.map((d) => d.waist).filter((v): v is number => v !== null),
    ...parsed.map((d) => d.hip).filter((v): v is number => v !== null),
    ...parsed.map((d) => d.neck).filter((v): v is number => v !== null),
  ];
  const maxVal = Math.max(...allValues);
  const minVal = Math.min(...allValues);
  const range = maxVal - minVal || 1;

  const W = 800,
    H = 300,
    PAD = 50;

  const toPoint = (index: number, value: number) => ({
    x:
      PAD +
      (parsed.length > 1 ? index / (parsed.length - 1) : 0.5) * (W - 2 * PAD),
    y: H - PAD - ((value - minVal) / range) * (H - 2 * PAD),
  });

  const buildPath = (values: (number | null)[]) => {
    const pts: { x: number; y: number }[] = [];
    values.forEach((v, i) => {
      if (v !== null) pts.push(toPoint(i, v));
    });
    if (pts.length < 2) return "";
    let path = `M ${pts[0].x},${pts[0].y}`;
    for (let i = 1; i < pts.length; i++) {
      const p = pts[i],
        prev = pts[i - 1];
      const cp1x = prev.x + (p.x - prev.x) * 0.4;
      path += ` C ${cp1x},${prev.y} ${p.x - (p.x - prev.x) * 0.4},${p.y} ${p.x},${p.y}`;
    }
    return path;
  };

  const waistPath = buildPath(parsed.map((d) => d.waist));
  const hipPath = buildPath(parsed.map((d) => d.hip));
  const neckPath = buildPath(parsed.map((d) => d.neck));

  const handleMouseMove = (e: React.MouseEvent<SVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = ((e.clientX - rect.left) / rect.width) * W;
    const closest = parsed.reduce((prev, curr, i) => {
      const px =
        PAD +
        (parsed.length > 1 ? i / (parsed.length - 1) : 0.5) * (W - 2 * PAD);
      const prevIdx = parsed.indexOf(prev);
      const prevX =
        PAD +
        (parsed.length > 1 ? prevIdx / (parsed.length - 1) : 0.5) *
          (W - 2 * PAD);
      return Math.abs(px - mouseX) < Math.abs(prevX - mouseX) ? curr : prev;
    });
    setHoveredPoint({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      visible: true,
      waist: closest.waist !== null ? String(closest.waist) : "-",
      hip: closest.hip !== null ? String(closest.hip) : "-",
      neck: closest.neck !== null ? String(closest.neck) : "-",
      date: format(
        new Date(closest._ts || closest._label),
        "MMM d, yyyy 'at' HH:mm",
      ),
      createdBy: closest._createdBy || undefined,
    });
  };

  return (
    <div className="relative w-full">
      {/* Legend */}
      <div className="flex gap-6 mb-3 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-6 h-0.5 bg-orange-500 rounded" />
          <span className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
            Waist
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-0.5 bg-violet-500 rounded" />
          <span className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
            Hip
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-0.5 bg-teal-500 rounded" />
          <span className={isDarkMode ? "text-gray-300" : "text-gray-600"}>
            Neck
          </span>
        </div>
      </div>

      <div className="relative w-full h-80">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className={`w-full h-full border rounded-lg cursor-crosshair overflow-visible ${
            isDarkMode
              ? "border-gray-700 bg-gray-900"
              : "border-gray-200 bg-white"
          }`}
          preserveAspectRatio="none"
          onMouseMove={handleMouseMove}
          onMouseLeave={() =>
            setHoveredPoint((p) => ({ ...p, visible: false }))
          }
        >
          <defs>
            <pattern
              id="circ-grid"
              width="40"
              height="30"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 40 0 L 0 0 0 30"
                fill="none"
                stroke={isDarkMode ? "#374151" : "#f0f0f0"}
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#circ-grid)" />

          {/* Axes */}
          <line
            x1={PAD}
            y1={H - PAD}
            x2={W - PAD}
            y2={H - PAD}
            stroke={isDarkMode ? "#6b7280" : "#d1d5db"}
            strokeWidth="1.5"
          />
          <line
            x1={PAD}
            y1={PAD}
            x2={PAD}
            y2={H - PAD}
            stroke={isDarkMode ? "#6b7280" : "#d1d5db"}
            strokeWidth="1.5"
          />

          {/* Waist line */}
          <path
            d={waistPath}
            fill="none"
            stroke="#f97316"
            strokeWidth="3"
            strokeLinecap="round"
            style={{ filter: "drop-shadow(0 2px 6px #f97316aa)" }}
          />
          {/* Hip line */}
          <path
            d={hipPath}
            fill="none"
            stroke="#8b5cf6"
            strokeWidth="3"
            strokeLinecap="round"
            style={{ filter: "drop-shadow(0 2px 6px #8b5cf6aa)" }}
          />
          {/* Neck line */}
          <path
            d={neckPath}
            fill="none"
            stroke="#14b8a6"
            strokeWidth="3"
            strokeLinecap="round"
            style={{ filter: "drop-shadow(0 2px 6px #14b8a6aa)" }}
          />

          {/* Dots - Waist */}
          {parsed.map((d, i) => {
            if (d.waist === null) return null;
            const p = toPoint(i, d.waist);
            return (
              <circle
                key={`w${i}`}
                cx={p.x}
                cy={p.y}
                r="4"
                fill="#f97316"
                stroke="white"
                strokeWidth="2"
              />
            );
          })}
          {/* Dots - Hip */}
          {parsed.map((d, i) => {
            if (d.hip === null) return null;
            const p = toPoint(i, d.hip);
            return (
              <circle
                key={`h${i}`}
                cx={p.x}
                cy={p.y}
                r="4"
                fill="#8b5cf6"
                stroke="white"
                strokeWidth="2"
              />
            );
          })}
          {/* Dots - Neck */}
          {parsed.map((d, i) => {
            if (d.neck === null) return null;
            const p = toPoint(i, d.neck);
            return (
              <circle
                key={`n${i}`}
                cx={p.x}
                cy={p.y}
                r="4"
                fill="#14b8a6"
                stroke="white"
                strokeWidth="2"
              />
            );
          })}

          {/* Y-axis labels */}
          {[0, 25, 50, 75, 100].map((pct) => {
            const val = minVal + (pct / 100) * range;
            const y = H - PAD - (pct / 100) * (H - 2 * PAD);
            return (
              <g key={pct}>
                <line
                  x1={PAD - 5}
                  y1={y}
                  x2={PAD}
                  y2={y}
                  stroke={isDarkMode ? "#6b7280" : "#d1d5db"}
                />
                <text
                  x={PAD - 10}
                  y={y + 3}
                  textAnchor="end"
                  fontSize="10"
                  className={isDarkMode ? "fill-gray-400" : "fill-gray-600"}
                >
                  {Math.round(val)}
                </text>
              </g>
            );
          })}

          {/* X-axis labels */}
          {parsed.map((d, i) => {
            const maxLabels = Math.min(8, parsed.length);
            const step = Math.max(1, Math.ceil(parsed.length / maxLabels));
            if (i % step !== 0 && i !== parsed.length - 1) return null;
            const x =
              PAD +
              (parsed.length > 1 ? i / (parsed.length - 1) : 0.5) *
                (W - 2 * PAD);
            const dateObj = new Date(d._ts || d._label);
            return (
              <g key={i}>
                <line
                  x1={x}
                  y1={H - PAD}
                  x2={x}
                  y2={H - PAD + 5}
                  stroke={isDarkMode ? "#6b7280" : "#d1d5db"}
                />
                <text
                  x={x}
                  y={H - PAD + 18}
                  textAnchor="middle"
                  fontSize="10"
                  className={isDarkMode ? "fill-gray-400" : "fill-gray-600"}
                >
                  {format(dateObj, "MMM d")}
                </text>
              </g>
            );
          })}

          {/* Hover line */}
          {hoveredPoint.visible && (
            <line
              x1={hoveredPoint.x / (W / 500)}
              y1={PAD}
              x2={hoveredPoint.x / (W / 500)}
              y2={H - PAD}
              stroke={isDarkMode ? "#6b7280" : "#9ca3af"}
              strokeWidth="1"
              strokeDasharray="4,4"
              opacity="0.7"
            />
          )}
        </svg>

        {/* Tooltip */}
        {hoveredPoint.visible && (
          <div
            className="absolute z-10 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg shadow-lg pointer-events-none"
            style={{
              left: hoveredPoint.x + 12,
              top: hoveredPoint.y - 10,
              transform: "translateY(-100%)",
            }}
          >
            <div className="mb-1 font-semibold">{hoveredPoint.date}</div>
            {hoveredPoint.createdBy && (
              <div className="mb-1 opacity-80">
                By: {hoveredPoint.createdBy}
              </div>
            )}
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-orange-400 rounded-full" />
              <span>
                Waist: <strong>{hoveredPoint.waist} cm</strong>
              </span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="inline-block w-2 h-2 rounded-full bg-violet-400" />
              <span>
                Hip: <strong>{hoveredPoint.hip} cm</strong>
              </span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="inline-block w-2 h-2 bg-teal-400 rounded-full" />
              <span>
                Neck: <strong>{hoveredPoint.neck} cm</strong>
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function VitalsDetailModal({
  isOpen,
  onOpenChange,
  vital,
  isDarkMode = false,
  loading = false,
  chartData = null,
}: VitalsDetailModalProps) {
  if (!vital) return null;
  const isNonNumericVital =
    vital.type === "consciousness-level" ||
    vital.type === "supplemental-oxygen";
  const vitalKey = getVitalKey(vital.type);
  const apiVital = chartData?.vitals?.[vitalKey];
  const transformVitalsForChart = (apiVital: any) => {
    if (!Array.isArray(apiVital?.values)) return [];

    return apiVital.values
      .slice()
      .sort(
        (a: any, b: any) =>
          new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime(),
      )
      .map((r: any) => {
        const ts = new Date(r.createdOn).toISOString();

        // Extract value for chart plotting (converted to common unit if needed)
        const readingUnit = r.unit || vital.unit;
        let chartValue = extractChartValue(r.values, vital.type, readingUnit);

        // Get display unit - empty string if unit is already in the value
        const displayUnit = getDisplayUnit(r.values, vital.type, readingUnit || vital.unit);

        // Validate chart value for specific vitals
        if (chartValue !== null && vital.type === "heart-rate") {
          // Heart rate should be between 40-200 BPM typically
          if (chartValue > 200 || chartValue < 40) {
            // This might be a unit conversion issue - check if dividing by 100 makes sense
            const corrected = chartValue / 100;
            if (corrected >= 40 && corrected <= 200) {
              chartValue = corrected;
            }
          }
        }

        return {
          date: ts,
          labelDate: new Date(r.createdOn).toLocaleDateString(),
          value: chartValue, // For chart plotting (normalized)
          displayValue: r.values, // For display (original format)
          displayUnit, // Original unit (or empty string if unit is in value)
          raw: r,
        };
      })
      .filter((d) => d.value !== null); // remove invalid entries
  };

  const apiHistoricalData = transformVitalsForChart(apiVital);
  const displayData =
    apiHistoricalData && apiHistoricalData.length > 0 ? apiHistoricalData : [];

  const validData = displayData.filter(
    (d) => typeof d.value === "number" && !isNaN(d.value),
  );

  // Fix the average value and unit
  const fixedAverage = fixAverageUnit(
    apiVital?.average,
    vital.type,
    vital.unit,
  );

  const totalReadings =
    typeof apiVital?.totalReadings === "number"
      ? apiVital.totalReadings
      : validData.length;

  // Get the latest reading value and unit as-is
  const latestReading = apiVital?.latestReading;
  let latestReadingValue =
    validData.length > 0 ? validData[validData.length - 1]?.displayValue : null;

  // Determine if we need to show the unit separately
  let latestReadingUnit = "";
  if (latestReading?.unit) {
    // Only show unit if it's not already part of the value
    if (latestReadingValue && typeof latestReadingValue === "string") {
      const strValue = latestReadingValue;
      if (
        vital.type === "height" &&
        (strValue.includes("ft") || strValue.includes("in"))
      ) {
        // Unit is already in the value, so don't show it separately
        latestReadingUnit = "";
      } else {
        latestReadingUnit = latestReading.unit;
      }
    } else {
      latestReadingUnit = latestReading.unit;
    }
  } else if (latestReadingValue && vital.type === "height") {
    // Check if unit is already part of the value
    const strValue = String(latestReadingValue);
    if (!strValue.includes("ft") && !strValue.includes("in")) {
      latestReadingUnit = "cm";
    }
  }

  // Prepare recent readings with original units
  const recentReadings: any[] = (() => {
    const readings = [];

    // Combine values from apiVital.values and recentReadings
    if (Array.isArray(apiVital?.values) && apiVital.values.length > 0) {
      const lastValues = apiVital.values.slice(-10).reverse(); // Get last 10, newest first
      readings.push(
        ...lastValues.map((reading: any) => {
          // Get display value and unit
          const displayValue = reading.values;
          const readingUnit = reading.unit || "";
          const displayUnit = readingUnit
            ? getDisplayUnit(reading.values, vital.type, readingUnit)
            : getDisplayUnit(reading.values, vital.type, vital.unit);

          return {
            values: displayValue,
            unit: displayUnit,
            createdOn: reading.createdOn,
            createdBy: reading.createdBy,
            raw: reading,
          };
        }),
      );
    }

    // Add recentReadings if available
    if (
      Array.isArray(apiVital?.recentReadings) &&
      apiVital.recentReadings.length > 0
    ) {
      readings.unshift(
        ...apiVital.recentReadings.map((reading: any) => {
          // Get the value
          const displayValue = reading.value || reading.values;

          // Determine the unit - check if unit is already in the value
          let unit = reading.unit;
          if (unit && displayValue) {
            // If the value already contains the unit text (e.g., "6 ft 6 in" contains "ft" and "in")
            const strValue = String(displayValue);
            if (
              vital.type === "height" &&
              (strValue.includes("ft") || strValue.includes("in"))
            ) {
              // Unit is already in the value, so don't show it separately
              unit = "";
            }
          } else if (!unit && displayValue) {
            unit = getDisplayUnit(displayValue, vital.type, vital.unit);
          }

          return {
            values: displayValue,
            unit: unit,
            createdOn: reading.createdOn,
            createdBy: reading.createdBy,
            raw: reading,
          };
        }),
      );
    }

    // Remove duplicates based on createdOn timestamp
    const uniqueReadings = readings.filter(
      (reading, index, self) =>
        index === self.findIndex((r) => r.createdOn === reading.createdOn),
    );
    // const removeReadingsWithin30Seconds = (readings: any[]) => {
    //   if (!readings?.length) return [];
    //   const sorted = [...readings].sort(
    //     (a, b) =>
    //       new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime(),
    //   );
    //   return sorted.filter((reading, index, arr) => {
    //     if (index === 0) return true;
    //     const current = new Date(reading.createdOn).getTime();
    //     const previous = new Date(arr[index - 1].createdOn).getTime();
    //     const diffSeconds = (current - previous) / 1000;
    //     return diffSeconds > 30;
    //   });
    // };

    // Apply deduplication based on value and time gap (less than 30 seconds)
    const deduplicatedReadings = deduplicateReadings(uniqueReadings, vital.type);

    // removeReadingsWithin30Seconds disabled — timestamp dedup above handles
    // true duplicates, and this filter was removing legitimate consecutive readings
    // const filteredReadings = removeReadingsWithin30Seconds(deduplicatedReadings);

    // Sort by date (newest first) and limit to 10
    return deduplicatedReadings
      .sort(
        (a, b) =>
          new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime(),
      )
      .slice(0, 10);
  })();

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={`max-w-6xl max-h-[90vh] overflow-hidden ${
          isDarkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"
        }`}
      >
        <DialogHeader className="pb-4 space-y-0 border-b">
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
            <Activity className="w-5 h-5 text-red-500" />
            {vital.label} History
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-8 h-[520px]">
          {/* Chart Section */}
          <div className="col-span-2 space-y-4">
            <div>
              <h3 className="mb-3 text-lg font-medium">Trend Over Time</h3>
              {loading ? (
                <Skeleton className="w-full h-80" />
              ) : isNonNumericVital ? (
                <div
                  className={`flex flex-col items-center justify-center h-80 rounded-lg border-2 border-dashed ${isDarkMode ? "border-gray-600 bg-gray-800" : "border-gray-300 bg-gray-50"}`}
                >
                  <div
                    className={`text-5xl font-bold mb-3 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {vital.value || "-"}
                  </div>
                  <div
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {vital.label}
                  </div>
                  {vital.type === "consciousness-level" && (
                    <div
                      className={`mt-4 text-xs text-center px-6 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                    >
                      AVPU Scale: Alert (0pts) · Voice (3pts) · Pain (3pts) ·
                      Unresponsive (3pts)
                    </div>
                  )}
                  {vital.type === "supplemental-oxygen" && (
                    <div
                      className={`mt-2 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                    >
                      {vital.value === "Yes"
                        ? "Adds +2 to NEWS2 score · SpO₂ scored on Scale 2"
                        : "No impact on NEWS2 score · SpO₂ scored on Scale 1"}
                    </div>
                  )}
                </div>
              ) : vital.type === "blood-pressure" ? (
                <BloodPressureChart
                  data={displayData}
                  isDarkMode={isDarkMode}
                />
              ) : vital.type === "circumference" ? (
                <CircumferenceChart
                  data={apiVital?.values ?? []}
                  isDarkMode={isDarkMode}
                />
              ) : (
                <InteractiveChart
                  data={displayData}
                  vital={vital}
                  isDarkMode={isDarkMode}
                  chartUnit={getChartUnit(vital.type)}
                />
              )}
            </div>

            {/* Statistics at bottom */}
            <div className="grid grid-cols-3 gap-4 mt-6">
              {loading ? (
                <>
                  <Skeleton className="h-20" />
                  <Skeleton className="h-20" />
                  <Skeleton className="h-20" />
                </>
              ) : isNonNumericVital ? (
                <div
                  className={`col-span-3 p-4 rounded-lg border text-center ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}
                >
                  <div
                    className={`text-sm font-medium mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
                  >
                    Total Readings
                  </div>
                  <div
                    className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {totalReadings}
                  </div>
                </div>
              ) : vital.type === "circumference" ? (
                (() => {
                  const parseCirc = (str: string) => {
                    const waistMatch = str.match(/Waist:\s*(\d+\.?\d*)/i);
                    const hipMatch = str.match(/Hip:\s*(\d+\.?\d*)/i);
                    const neckMatch = str.match(/Neck:\s*(\d+\.?\d*)/i);
                    return {
                      waist: waistMatch ? waistMatch[1] : null,
                      hip: hipMatch ? hipMatch[1] : null,
                      neck: neckMatch ? neckMatch[1] : null,
                    };
                  };
                  const sortedValues = (apiVital?.values ?? [])
                    .slice()
                    .sort(
                      (a: any, b: any) =>
                        new Date(a.createdOn).getTime() -
                        new Date(b.createdOn).getTime(),
                    );
                  const latestStr = String(
                    sortedValues[sortedValues.length - 1]?.values ?? "",
                  );
                  const latest = parseCirc(latestStr);

                  const avg = (field: "waist" | "hip" | "neck") => {
                    const nums = sortedValues
                      .map((d: any) => parseCirc(String(d.values ?? ""))[field])
                      .filter((v): v is string => v !== null)
                      .map(Number)
                      .filter((n) => !isNaN(n));
                    return nums.length
                      ? Math.round(
                          nums.reduce((a, b) => a + b, 0) / nums.length,
                        )
                      : null;
                  };
                  const avgWaist = avg("waist");
                  const avgHip = avg("hip");
                  const avgNeck = avg("neck");

                  return (
                    <>
                      <div
                        className={`p-4 rounded-lg border text-center ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}
                      >
                        <div className="mb-1 text-sm font-medium text-gray-600">
                          Latest Reading
                        </div>
                        <div
                          className={`text-md font-bold space-y-0.5 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {latest.waist && <div>Waist: {latest.waist} cm</div>}
                          {latest.hip && <div>Hip: {latest.hip} cm</div>}
                          {latest.neck && <div>Neck: {latest.neck} cm</div>}
                          {!latest.waist && !latest.hip && !latest.neck && (
                            <div>-</div>
                          )}
                        </div>
                      </div>
                      <div
                        className={`p-4 rounded-lg border text-center ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}
                      >
                        <div className="mb-1 text-sm font-medium text-gray-600">
                          Average
                        </div>
                        <div
                          className={`text-md font-bold space-y-0.5 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {avgWaist !== null && <div>Waist: {avgWaist} cm</div>}
                          {avgHip !== null && <div>Hip: {avgHip} cm</div>}
                          {avgNeck !== null && <div>Neck: {avgNeck} cm</div>}
                          {avgWaist === null &&
                            avgHip === null &&
                            avgNeck === null && <div>-</div>}
                        </div>
                      </div>
                      <div
                        className={`p-4 rounded-lg border text-center ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}
                      >
                        <div className="mb-1 text-sm font-medium text-gray-600">
                          Total Readings
                        </div>
                        <div
                          className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {totalReadings || sortedValues.length}
                        </div>
                      </div>
                    </>
                  );
                })()
              ) : vital.type === "blood-pressure" ? (
                (() => {
                  const parseBP = (val: any) => {
                    const str = String(val ?? "");
                    const [sys, dia] = str.split("/");
                    return { sys: sys?.trim() ?? "-", dia: dia?.trim() ?? "-" };
                  };
                  const latest = parseBP(latestReadingValue);
                  const diaValues = (apiVital?.values ?? [])
                    .map((r: any) => Number(String(r.values).split("/")[1]))
                    .filter((n: number) => !isNaN(n));
                  const avgDia = diaValues.length
                    ? Math.round(
                        diaValues.reduce((a: number, b: number) => a + b, 0) /
                          diaValues.length,
                      )
                    : "-";

                  return (
                    <>
                      <div
                        className={`p-4 rounded-lg border text-center ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}
                      >
                        <div className="mb-1 text-sm font-medium text-gray-600">
                          Latest Reading
                        </div>
                        <div className="text-2xl font-bold">
                          <span
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {latest.sys}
                          </span>
                          <span className="mx-1 text-xl font-normal text-gray-400">
                            /
                          </span>
                          <span
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {latest.dia}
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          mmHg · Sys / Dia
                        </div>
                      </div>
                      <div
                        className={`p-4 rounded-lg border text-center ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}
                      >
                        <div className="mb-1 text-sm font-medium text-gray-600">
                          Average
                        </div>
                        <div className="text-2xl font-bold">
                          <span
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {fixedAverage.value}
                          </span>
                          <span className="mx-1 text-xl font-normal text-gray-400">
                            /
                          </span>
                          <span
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {avgDia}
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          mmHg · Sys / Dia
                        </div>
                      </div>
                      <div
                        className={`p-4 rounded-lg border text-center ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}
                      >
                        <div className="mb-1 text-sm font-medium text-gray-600">
                          Total Readings
                        </div>
                        <div
                          className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {totalReadings}
                        </div>
                      </div>
                    </>
                  );
                })()
              ) : (
                <>
                  <div
                    className={`p-4 rounded-lg border text-center ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}
                  >
                    <div className="mb-1 text-sm font-medium text-gray-600">
                      Latest Reading
                    </div>
                    {/* <div className="text-2xl font-bold text-gray-900"> // temporary
                      {latestReadingValue ?? "-"} {latestReadingUnit}
                    </div> */}
                    <div
                      className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {latestReadingValue ?? "-"}
                      {latestReadingUnit && latestReadingUnit.trim() !== ""
                        ? ` ${latestReadingUnit}`
                        : vital.unit
                          ? ` ${vital.unit}`
                          : ""}
                    </div>
                  </div>
                  <div
                    className={`p-4 rounded-lg border text-center ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}
                  >
                    <div className="mb-1 text-sm font-medium text-gray-600">
                      Average
                    </div>
                    {/* <div className="text-2xl font-bold text-gray-900"> /// temporary
                      {fixedAverage.value} {fixedAverage.unit}
                    </div> */}
                    <div
                      className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {fixedAverage.value}
                      {fixedAverage.unit && fixedAverage.unit.trim() !== ""
                        ? ` ${fixedAverage.unit}`
                        : vital.unit
                          ? ` ${vital.unit}`
                          : ""}
                    </div>
                    {vital.type === "height" && fixedAverage.unit === "cm" && (
                      <div className="mt-1 text-xs text-gray-500">
                        (Normalized to cm)
                      </div>
                    )}
                  </div>
                  <div
                    className={`p-4 rounded-lg border text-center ${isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}
                  >
                    <div className="mb-1 text-sm font-medium text-gray-600">
                      Total Readings
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {totalReadings}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Recent Readings Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Recent Readings</h3>
            <div className="space-y-4 h-[420px] overflow-y-auto">
              {loading
                ? Array.from({ length: 4 }).map((_, idx) => (
                    <Skeleton key={idx} className="h-24" />
                  ))
                : (recentReadings?.length > 0 ? recentReadings : []).map(
                    (reading: any, index: number) => {
                      // Clean up the value display - ensure no duplicate units
                      let displayValue = reading.values ?? reading.value ?? "-";
                      let displayUnit = reading?.unit || "";

                      // Final safety check: if value contains ft/in, don't show unit
                      if (
                        vital.type === "height" &&
                        typeof displayValue === "string"
                      ) {
                        if (
                          displayValue.includes("ft") ||
                          displayValue.includes("in")
                        ) {
                          displayUnit = "";
                        }
                      }

                      return (
                        <div
                          key={index}
                          className={`p-4 rounded-lg border ${
                            isDarkMode
                              ? "bg-gray-800 border-gray-700"
                              : "bg-gray-50 border-gray-200"
                          }`}
                        >
                          {vital.type === "blood-pressure" ? (
                            (() => {
                              const [sys, dia] =
                                String(displayValue).split("/");
                              return (
                                <div className="mb-2">
                                  <span
                                    className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                  >
                                    {sys?.trim()}
                                  </span>
                                  <span className="mx-1 text-xl font-normal text-gray-400">
                                    /
                                  </span>
                                  <span
                                    className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                                  >
                                    {dia?.trim()}
                                  </span>
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    mmHg · Systolic / Diastolic
                                  </div>
                                </div>
                              );
                            })()
                          ) : vital.type === "supplemental-oxygen" ? (
                            (() => {
                              const yesNo = normaliseYesNo(displayValue);
                              const isYes = yesNo === "Yes";
                              return (
                                <div className="mb-2">
                                  <span
                                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${
                                      isYes
                                        ? "bg-sky-100 text-sky-800"
                                        : isDarkMode
                                          ? "bg-gray-700 text-gray-300"
                                          : "bg-gray-100 text-gray-700"
                                    }`}
                                  >
                                    {yesNo}
                                  </span>
                                  <div
                                    className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                                  >
                                    {isYes
                                      ? "On supplemental oxygen (+2 NEWS2)"
                                      : "Breathing room air"}
                                  </div>
                                </div>
                              );
                            })()
                          ) : (
                            <div
                              className={`mb-2 text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                            >
                              {displayValue}
                              {displayUnit && displayUnit.trim() !== ""
                                ? ` ${displayUnit}`
                                : ""}
                            </div>
                          )}

                          <div className="space-y-2 text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              {format(
                                new Date(reading.createdOn || reading.date),
                                "MMMM do, yyyy 'at' HH:mm",
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4" />
                              {reading.createdBy ||
                                reading.raw?.createdBy ||
                                apiVital?.latestReading?.createdBy ||
                                "-"}
                            </div>
                          </div>
                        </div>
                      );
                    },
                  )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
